import { useCallback, useEffect, useMemo, useRef } from 'react';

// Physical scanners emit the raw encoded digits, sometimes padded with stray spaces. Barcodes are
// numeric, so stripping all whitespace is safe and makes a padded read match what PAS stored.
const normalize = (s) => String(s == null ? '' : s).replace(/\s/g, '');
const stripZeros = (s) => normalize(s).replace(/^0+/, '');

/**
 * Scan-to-Assign. Captures a badge scan (keyboard-wedge scanners type fast then press Enter),
 * resolves it against associateInfo[*].badgeBarcodeId (persisted on the ShiftPlan bean, so it loads
 * with the plan), and hands the matched associate to a callback so the board can open that
 * associate's assign card.
 *
 * Resolution is fully local, so a scan only matches associates currently on the board. A badge not in
 * the model falls through to onScanUnresolved.
 *
 * For testing without a physical scanner, window.replicateBadgeScan(barcode) drives the same
 * resolution path as a real scan.
 *
 * @param {Object}   params.associateInfo     map of resourceId -> associate (from shiftAllocationModel)
 * @param {Function} params.onScanResolved    (associate, resourceId, barcode) => void
 * @param {Function} params.onScanUnresolved  (barcode, { ambiguous, candidateCount }) => void
 * @param {boolean}  params.enabled           toggle the keyboard-wedge listener (default true). Pass false
 *                                            while a modal is open so a stray scan cannot fire behind it.
 * @param {boolean}  params.targetArmed       true when a scan target is armed. Space is then claimed by
 *                                            the scan even as the first keystroke, since a barcode padded
 *                                            with a leading space would otherwise toggle that target.
 * @param {number}   params.minLength         min chars to treat a burst as a scan (default 3)
 */
export function useBadgeScan({
  associateInfo,
  onScanResolved,
  onScanUnresolved,
  enabled = true,
  minLength = 3,
  targetArmed = false
}) {
  // Everything `resolve` reads is held in a ref so `resolve` - and therefore the keydown/paste
  // listeners - keep a stable identity for the life of the board. Re-registering those listeners
  // resets the in-progress keystroke buffer, which silently truncates or drops a scan that happens to
  // straddle a render. Both inputs churn constantly in practice: callers pass inline callbacks (new
  // identity every render), and the enrichment poll replaces `associateInfo` wholesale on a timer.
  const callbacksRef = useRef({ onScanResolved, onScanUnresolved });
  useEffect(() => {
    callbacksRef.current = { onScanResolved, onScanUnresolved };
  }, [onScanResolved, onScanUnresolved]);

  // barcode -> every resourceId that could have produced it, counting both the exact barcode and its
  // zero-stripped form. A key with more than one associate behind it is ambiguous and is never
  // guessed: see resolve().
  const index = useMemo(() => {
    const map = {};
    const add = (key, resourceId) => {
      if (!key) return;
      if (!map[key]) map[key] = new Set();
      map[key].add(resourceId);
    };
    Object.entries(associateInfo || {}).forEach(([resourceId, a]) => {
      const bc = a && a.badgeBarcodeId;
      if (bc) {
        add(normalize(bc), resourceId);
        // Indexed so a scanner that drops leading zeros still matches.
        add(stripZeros(bc), resourceId);
      }
    });
    return map;
  }, [associateInfo]);

  const indexRef = useRef(index);
  const associateInfoRef = useRef(associateInfo);
  const targetArmedRef = useRef(targetArmed);
  useEffect(() => {
    indexRef.current = index;
    associateInfoRef.current = associateInfo;
    targetArmedRef.current = targetArmed;
  }, [index, associateInfo, targetArmed]);

  const resolve = useCallback(
    (rawBarcode) => {
      const bc = normalize(rawBarcode);
      if (!bc) return;
      const idx = indexRef.current || {};
      const info = associateInfoRef.current;
      const candidates = idx[bc] || idx[stripZeros(bc)];
      const { onScanResolved: onResolved, onScanUnresolved: onUnresolved } = callbacksRef.current;

      // Ambiguous scan: the digits match more than one associate on this board, which happens when a
      // scanner drops a leading zero and the stripped form is another associate's real badge
      // ("01234567" -> "1234567"). There is no way to tell which badge was actually presented, so
      // refuse rather than place the wrong person and let the planner fall back to searching by name.
      if (candidates && candidates.size > 1) {
        if (onUnresolved) onUnresolved(bc, { ambiguous: true, candidateCount: candidates.size });
        return;
      }

      const resourceId = candidates && candidates.size === 1 ? candidates.values().next().value : undefined;
      if (resourceId && info && info[resourceId]) {
        if (onResolved) onResolved(info[resourceId], resourceId, bc);
      } else if (onUnresolved) {
        onUnresolved(bc, { ambiguous: false, candidateCount: 0 });
      }
    },
    // Deliberately empty: every input is read through a ref, so the listeners never re-register.
    []
  );

  // Capture two ways a badge can arrive:
  //  1. keyboard-wedge scanner: a fast burst of keystrokes ending in Enter
  //  2. copy-paste (Cmd/Ctrl+V): a single paste event carrying the barcode text
  // Both ignore editable fields so real typing/pasting into inputs is unaffected.
  useEffect(() => {
    if (!enabled) return undefined;
    let buffer = '';
    let lastTs = 0;
    const INTERKEY_MS = 50; // scanners burst < ~30ms/char; slower human typing resets the buffer
    const isEditableTarget = (t) => {
      const tag = (t && t.tagName) || '';
      return tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable);
    };
    // A scan-arming control (card header / empty slot) marks itself with data-scan-target, so the space
    // guard below can apply to those elements alone rather than to the whole page.
    const isScanTargetControl = (t) => Boolean(t && t.closest && t.closest('[data-scan-target="true"]'));
    const onKeyDown = (e) => {
      if (isEditableTarget(e.target)) return;
      const now = Date.now();
      const continuesBurst = now - lastTs <= INTERKEY_MS && buffer.length > 0;
      if (!continuesBurst) buffer = '';
      lastTs = now;
      if (e.key === 'Enter') {
        if (buffer.length >= minLength) {
          // The scan owns this Enter. Without preventDefault + stopPropagation a focused control also
          // acts on it: arming a scan target leaves that card header or slot focused, and its own
          // Enter handler would toggle the target back off before the scan resolved - so the scan
          // would find nothing armed and fall back to opening the assign card. Listening in the
          // capture phase (below) is what lets us stop the event before the focused element sees it.
          e.preventDefault();
          e.stopPropagation();
          resolve(buffer);
        }
        // A burst too short to be a scan is left alone, so Enter still activates a focused control.
        buffer = '';
        return;
      }
      if (e.key && e.key.length === 1) {
        buffer += e.key;
        // A leading pad space arrives as the FIRST keystroke, so `continuesBurst` is false for it. Claim
        // it only when focus is on an armed scan target, where the barcode's own padding would toggle
        // that target off. Scoped deliberately: a card stays armed indefinitely for bulk scanning, so
        // swallowing space page-wide would break page scrolling and every other focusable control.
        // Enter still reaches the control, so it can always be released from the keyboard.
        if (e.key === ' ' && targetArmedRef.current && isScanTargetControl(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // Mid-burst characters belong to the scan. A barcode padded with spaces would otherwise send
        // a space to the focused control, and both scan targets are role="button" elements that
        // activate on space - so a padded barcode would toggle the armed target mid-scan.
        if (continuesBurst) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    const onPaste = (e) => {
      if (isEditableTarget(e.target)) return;
      const text =
        ((e.clipboardData || window.clipboardData) && (e.clipboardData || window.clipboardData).getData('text')) || '';
      const trimmed = text.trim();
      if (trimmed.length >= minLength) resolve(trimmed);
    };
    // Capture phase: a scan must be seen before any focused control reacts to the same keystrokes.
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('paste', onPaste);
    };
  }, [enabled, minLength, resolve]);

  // Console hook for driving a scan without scanner hardware. It only replays a barcode through the
  // same resolution path a real scan uses, so it can't produce a match the board wouldn't already make.
  useEffect(() => {
    window.replicateBadgeScan = (barcode) => resolve(barcode);
    return () => {
      delete window.replicateBadgeScan;
    };
  }, [resolve]);

  return { resolve, replicateScan: resolve };
}

export default useBadgeScan;
